import { CopyButton, CreateButton, DeleteButton, EditButton, ViewButton } from '@/components/common';
import { Page } from '@/components/dashboard/page';
import { createFileRoute } from '@tanstack/react-router';
import { Code, Heading, ModalButton, Row, Small } from 'ht-sdk/react';
import { useState } from 'react';
import { FilterControls } from '@/components/filter-controls';
import { api } from '@/api';
import type { Location } from '@shared/modelTypes/location-types';
import { Table } from '@/components/table';
import { toast } from 'sonner';
import { router } from '@/main';
import { useSession } from 'ht-sdk/auth/client';
import { auth } from '@/auth';

export const Route = createFileRoute('/dashboard/locations/')({
	component: RouteComponent,
	loader: async () => {
		// TODO: fix types so you don't need to pass an empty object
		// TODO: investigate weird performance issue
		// this gets slow with 100+ locations
		// seems to be an issue with the auth fetch code?
		const locations = await api.locationList({});

		return {
			locations: locations.success ? locations.data : [],
		};
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();
	// TODO:Consider bundling FilterControls and Table into a FilterTable component
	const [locations, setLocations] = useState<Location[]>(data.locations);
	const session = useSession(auth);
	const isAdmin = session?.role == 'admin';

	return (
		<Page>
			<Row gap={3}>
				<Heading>Locations</Heading>
				{isAdmin && <CreateButton to="/dashboard/locations/new">New Location</CreateButton>}
			</Row>
			<FilterControls
				items={data.locations}
				onChanged={setLocations}
				getSearchableText={(s) => s.name}
				sortOptions={[
					{ key: 'name', label: 'Name', sortFn: (a, b) => a.name.localeCompare(b.name) },
					{ key: 'code', label: 'Code', sortFn: (a, b) => a.code.localeCompare(b.code) },
				]}
			/>
			{locations.length === 0 && <Small>No locations found.</Small>}
			{locations.length > 0 && <LocationTable locations={locations} setLocations={setLocations} isAdmin={isAdmin} />}
		</Page>
	);
}

export function LocationTable({
	locations,
	setLocations,
	isAdmin,
}: {
	locations: Location[];
	setLocations: (locations: Location[]) => void;
	isAdmin: boolean;
}) {
	return (
		<Table
			headers={['Name', 'Code', 'Actions']}
			rows={locations.map((s) => [
				s.name,
				<Code>{s.code}</Code>,
				<Row gap={1}>
					<ViewButton to="/dashboard/locations/$id/view" params={{ id: s.id }} />
					{isAdmin && <EditButton to="/dashboard/locations/$id/edit" params={{ id: s.id }} />}
					{/* TODO: fix as={ModalButton} on inheriting other button props */}
					{isAdmin && (
						<CopyButton
							as={ModalButton}
							onAccept={async () => {
								const result = await api.locationDuplicate({ id: s.id });
								if (result.success) {
									router.navigate({
										to: '/dashboard/locations/$id/edit',
										params: { id: result.data.id },
									});
									toast.success('Location duplicated successfully');
								} else {
									toast.error('Failed to duplicate location: ' + result.message);
								}
							}}
							className="cursor-pointer"
						/>
					)}
					{isAdmin && (
						<DeleteButton
							as={ModalButton}
							onAccept={async () => {
								const result = await api.locationDelete({ id: s.id });
								if (result.success) {
									const locations = await api.locationList({});
									if (locations.success) setLocations(locations.data);
									toast.success('Location deleted successfully');
								} else {
									toast.error('Failed to delete location: ' + result.message);
								}
							}}
							className="cursor-pointer"
						/>
					)}
				</Row>,
			])}
		/>
	);
}
